import React, { useState, useEffect, useCallback } from "react";
import "./OnboardingWizard.scss";
import Copyright from "../../components/Copyright/Copyright";

// Import wizard pages
import UsageTypePage from "./pages/UsageTypePage";
import FunctionalityPage from "./pages/FunctionalityPage";
import CompanyCountPage from "./pages/CompanyCountPage";
import InvoiceVolumePage from "./pages/InvoiceVolumePage";
import CustomerInvoiceCountPage from "./pages/CustomerInvoiceCountPage";
import SupplierInvoiceCountPage from "./pages/SupplierInvoiceCountPage";
import SupportSelectionPage from "./pages/SupportSelectionPage";
import PricingCalculationPage from "./pages/PricingCalculationPage";
import SubmissionPage from "./pages/SubmissionPage";

const OnboardingWizard = () => {
  // Wizard state management
  const [currentStep, setCurrentStep] = useState(1);
  const [wizardData, setWizardData] = useState({
    usageType: "",
    existingSystem: "",
    selectedFeatures: [],
    companyCount: "",
    selectedOption: "",
    invoiceVolume: "",
    customerInvoiceCount: "",
    supplierInvoiceCount: "",
    supportType: "",
    billingPeriod: "yearly",
    email: "",
    companyName: "",
    country: "",
    allTermsAccepted: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [statusMessage, setStatusMessage] = useState({ text: '', type: '' });

  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo(0, 0);
  }, []);

  // Scroll to top whenever the step changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [currentStep]);

  // Helper function to check if specific features are selected
  const hasElectronicInvoicing = wizardData.selectedFeatures.includes('electronic-invoicing');
  const hasInvoiceProcessing = wizardData.selectedFeatures.includes('invoice-processing');
  const hasDigitalSigning = wizardData.selectedFeatures.includes('digital-signing-id-card') || 
                           wizardData.selectedFeatures.includes('digital-signing-smart-id');
  const hasInvoiceFeatures = hasElectronicInvoicing || hasInvoiceProcessing;
  const hasSupplierFeatures = hasInvoiceProcessing || hasDigitalSigning;

  // Calculate total steps based on selected features
  const calculateTotalSteps = () => {
    let steps = 6; // Usage Type, Company Count, Functionality, Support (for standalone), Pricing, Submission (always present)
    
    if (wizardData.usageType === 'standalone' && !hasInvoiceFeatures && !hasDigitalSigning) {
      steps += 1; // Add Invoice Volume page for standalone users without specific invoice features
    } else if (hasInvoiceFeatures || hasDigitalSigning) {
      if (hasElectronicInvoicing) steps += 1; // Customer Invoice Count
      if (hasSupplierFeatures) steps += 1; // Supplier Invoice Count
    }
    
    // Support page is only shown for standalone systems
    if (wizardData.usageType === 'with-existing') {
      steps -= 1; // Remove support page for existing system users
    }
    
    return steps;
  };

  const totalSteps = calculateTotalSteps();

  // Determine the actual step based on usage type and selected features
  const getActualStep = (step) => {
    let stepCounter = 1;

    // Step 1: Usage Type (always)
    if (stepCounter === step) return 1;
    stepCounter++;

    // Step 2: Company Count (always)
    if (stepCounter === step) return 4; // Company Count
    stepCounter++;

    // Step 3: Functionality (always)
    if (stepCounter === step) return 2;
    stepCounter++;

    // Step 4+: Conditional pages based on features
    if (wizardData.usageType === 'standalone' && !hasInvoiceFeatures && !hasDigitalSigning) {
      // Show Invoice Volume page for standalone users without specific invoice features
      if (stepCounter === step) return 3; // Invoice Volume
      stepCounter++;
    } else if (hasInvoiceFeatures || hasDigitalSigning) {
      // Show specific invoice count pages
      if (hasElectronicInvoicing) {
        if (stepCounter === step) return 5; // Customer Invoice Count
        stepCounter++;
      }
      if (hasSupplierFeatures) {
        if (stepCounter === step) return 6; // Supplier Invoice Count
        stepCounter++;
      }
    }

    // Support Selection (only for standalone systems)
    if (wizardData.usageType === 'standalone') {
      if (stepCounter === step) return 8; // Support Selection
      stepCounter++;
    }

    // Pricing Calculation (before submission)
    if (stepCounter === step) return 7; // Pricing Calculation
    stepCounter++;

    // Final step: Submission (always last)
    if (stepCounter === step) return 9; // Submission

    return step;
  };

  // Get the actual step number for current step
  const actualStep = getActualStep(currentStep);

  // Navigation functions
  const goToNextStep = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    }
  };

  const goToPreviousStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  // Update wizard data
  const updateWizardData = useCallback((stepData) => {
    setWizardData(prev => {
      const newData = {
        ...prev,
        ...stepData
      };
      return newData;
    });
  }, []);

  // Submit wizard data
  const submitWizardData = async () => {
    setIsSubmitting(true);
    setStatusMessage({ text: 'Submitting your information...', type: 'info' });

    try {
      // TODO: Replace with actual backend endpoint
      const response = await fetch('/api/onboarding/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(wizardData)
      });

      if (response.ok) {
        setStatusMessage({ 
          text: '✅ Thank you! Your information has been submitted successfully. We\'ll be in touch soon!', 
          type: 'success' 
        });
        
        // Reset wizard after successful submission
        setTimeout(() => {
          setCurrentStep(1);
          setWizardData({
            usageType: "",
            existingSystem: "",
            selectedFeatures: [],
            companyCount: "",
            selectedOption: "",
            invoiceVolume: "",
            customerInvoiceCount: "",
            supplierInvoiceCount: "",
            billingPeriod: "yearly",
            email: "",
          });
          setStatusMessage({ text: '', type: '' });
        }, 3000);
      } else {
        throw new Error('Failed to submit data');
      }
    } catch (error) {
      setStatusMessage({ 
        text: '❌ Sorry, there was an error submitting your information. Please try again.', 
        type: 'error' 
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Render current step
  const renderCurrentStep = () => {
    switch (actualStep) {
      case 1:
        return (
          <UsageTypePage
            data={wizardData}
            onUpdate={updateWizardData}
          />
        );
      case 2:
        return (
          <FunctionalityPage
            data={wizardData}
            onUpdate={updateWizardData}
          />
        );
      case 3:
        return (
          <InvoiceVolumePage
            data={wizardData}
            onUpdate={updateWizardData}
          />
        );
      case 4:
        return (
          <CompanyCountPage
            data={wizardData}
            onUpdate={updateWizardData}
          />
        );
      case 5:
        return (
          <CustomerInvoiceCountPage
            data={wizardData}
            onUpdate={updateWizardData}
          />
        );
      case 6:
        return (
          <SupplierInvoiceCountPage
            data={wizardData}
            onUpdate={updateWizardData}
          />
        );
      case 7:
        return (
          <PricingCalculationPage
            data={wizardData}
            onUpdate={updateWizardData}
          />
        );
      case 8:
        return (
          <SupportSelectionPage
            data={wizardData}
            onUpdate={updateWizardData}
          />
        );
      case 9:
        return (
          <SubmissionPage
            data={wizardData}
            onUpdate={updateWizardData}
          />
        );
      default:
        return (
          <UsageTypePage
            data={wizardData}
            onUpdate={updateWizardData}
          />
        );
    }
  };

  // Check if current step is valid
  const isCurrentStepValid = () => {
    const isValid = (() => {
      switch (actualStep) {
        case 1:
          return wizardData.usageType && 
                 (wizardData.usageType === 'standalone' || 
                  (wizardData.usageType === 'with-existing' && wizardData.existingSystem && wizardData.existingSystem.trim()));
        case 2:
          // Functionality page - always valid (no validation needed)
          return true;
        case 3:
          // Invoice Volume page - only shown for standalone users without specific invoice features
          return wizardData.invoiceVolume && wizardData.invoiceVolume.trim() !== '';
        case 4:
          // Company Count page - valid if a quick select option is chosen (1-5) or if "more" is selected with valid input
          if (wizardData.selectedOption && ['1', '2', '3', '4', '5'].includes(wizardData.selectedOption)) {
            return true;
          }
          if (wizardData.selectedOption === 'more') {
            return wizardData.companyCount && parseInt(wizardData.companyCount) >= 1;
          }
          return false;
        case 5:
          // Customer Invoice Count page - shown when Electronic Invoicing is selected
          return wizardData.customerInvoiceCount && wizardData.customerInvoiceCount.trim() !== '';
        case 6:
          // Supplier Invoice Count page - shown when Incoming Invoice Processing or Digital Signing is selected
          return wizardData.supplierInvoiceCount && wizardData.supplierInvoiceCount.trim() !== '';
        case 7:
          // Pricing Calculation page - always valid (no validation needed)
          return true;
        case 8:
          // Support Selection page - valid if a support type is selected
          return wizardData.supportType && wizardData.supportType.trim() !== '';
        case 9:
          // Submission page - valid if all fields are filled and terms are accepted
          // Note: The submission page handles its own validation and captcha, so we just check basic requirements
          return wizardData.email && wizardData.email.trim() !== '' &&
                 wizardData.companyName && wizardData.companyName.trim() !== '' &&
                 wizardData.country && wizardData.country.trim() !== '' &&
                 wizardData.allTermsAccepted;
        default:
          return false;
      }
    })();
    
    return isValid;
  };

  return (
    <div className="onboarding-wizard">
      {/* Header Section */}
      <section className="wizard-header">
        <div className="container">
          <div className="wizard-header-content">
            <h1>Welcome to CashManager</h1>
            <p>Let's get you set up with the perfect configuration.</p>
            
            {/* Progress Bar */}
            <div className="progress-container">
              <div className="progress-bar">
                <div 
                  className="progress-fill" 
                  style={{ width: `${(currentStep / totalSteps) * 100}%` }}
                ></div>
              </div>
              <div className="progress-text">
                Step {currentStep} of {totalSteps}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Wizard Content */}
      <section className="wizard-content">
        <div className="container">
          <div className="wizard-card">
            {renderCurrentStep()}
            
            {/* Navigation - Hide Next/Submit button on submission page (step 9) as it has its own submit button */}
            <div className="wizard-navigation" id="wizard-navigation">
              <div className="nav-buttons">
                <div className="nav-left">
                  {currentStep > 1 && (
                    <button 
                      className="btn btn-secondary"
                      onClick={goToPreviousStep}
                      disabled={isSubmitting}
                    >
                      Previous
                    </button>
                  )}
                </div>
                
                {actualStep !== 9 && (
                  <div className="nav-right">
                    {currentStep < totalSteps ? (
                      <button 
                        className="btn btn-primary"
                        onClick={goToNextStep}
                        disabled={!isCurrentStepValid() || isSubmitting}
                      >
                        Next
                      </button>
                    ) : (
                      <button 
                        className="btn btn-primary"
                        onClick={submitWizardData}
                        disabled={!isCurrentStepValid() || isSubmitting}
                      >
                        {isSubmitting ? 'Submitting...' : 'Complete setup'}
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Status Message */}
            {statusMessage.text && (
              <div className={`status ${statusMessage.type}`}>
                {statusMessage.text}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Copyright Section */}
      <Copyright />
    </div>
  );
};

export default OnboardingWizard;
